import { useTranslation } from "react-i18next";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { Controller, useFormContext } from "react-hook-form";
import { Box, FormLabel, Typography, Checkbox } from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

import { days, DayType } from "../../../../../../../../enums/days.enum";

import { AddEntityLocationsFormData } from "../../../../../../types/editEntityProfile-type";

const formatTimeString = (date: Date | null): string => {
  if (!date) return "00:00";
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  return `${hours}:${minutes}`;
};

const WorkingHoursTab = ({ locationIdx, branchIdx }: { locationIdx: number; branchIdx: number }) => {
  const { t } = useTranslation("profiles", { keyPrefix: "entityProfileEdit" });

  return (
    <Box sx={styles.container}>
      <Typography sx={styles.title}>{t("workingHours")}</Typography>
      {days.map((day) => (
        <WorkingHoursField key={day} day={day as DayType} locationIdx={locationIdx} branchIdx={branchIdx} />
      ))}
    </Box>
  );
};

export default WorkingHoursTab;

const WorkingHoursField = ({
  day,
  locationIdx,
  branchIdx,
}: {
  day: DayType;
  locationIdx: number;
  branchIdx: number;
}) => {
  const { t } = useTranslation("profiles", { keyPrefix: "entityProfileEdit" });
  const { t: tDays } = useTranslation("common", { keyPrefix: "day" });
  const { control } = useFormContext<AddEntityLocationsFormData>();

  return (
    <Controller
      name={`locations.${locationIdx}.branches.${branchIdx}.workingHours.${day}`}
      control={control}
      defaultValue={undefined}
      render={({ field: { value, onChange }, fieldState: { error } }) => (
        <Box sx={styles.dayRow}>
          <Box sx={styles.dayLabel}>
            <Checkbox
              size="small"
              checked={!!value}
              onChange={(e) => {
                if (!e.target.checked) {
                  onChange(undefined);
                } else {
                  onChange({
                    openingTime: "09:00",
                    closingTime: "17:00",
                  });
                }
              }}
            />
            <FormLabel sx={styles.dayLabel}>{tDays(day)}</FormLabel>
          </Box>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <TimePicker
              disabled={!value}
              value={value?.openingTime ? new Date(`1970-01-01T${value.openingTime}`) : null}
              onChange={(newValue) => {
                if (newValue) {
                  onChange({
                    ...value,
                    openingTime: formatTimeString(newValue),
                  });
                }
              }}
              slotProps={{
                textField: {
                  error: !!error,
                  helperText: error?.message && t(error.message),
                  size: "small",
                  sx: {
                    width: "150px",
                  },
                },
              }}
            />
          </LocalizationProvider>
          <Typography sx={styles.timeLabel}>{t("to")}</Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <TimePicker
              disabled={!value}
              value={value?.closingTime ? new Date(`1970-01-01T${value.closingTime}`) : null}
              onChange={(newValue) => {
                if (newValue) {
                  onChange({
                    ...value,
                    closingTime: formatTimeString(newValue),
                  });
                }
              }}
              slotProps={{
                textField: {
                  error: !!error,
                  helperText: error?.message && t(error.message),
                  size: "small",
                  sx: {
                    width: "150px",
                  },
                },
              }}
            />
          </LocalizationProvider>
        </Box>
      )}
    />
  );
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  title: {
    color: "var(--Gray-900, #101828)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
  },
  dayContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  dayRow: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  dayLabel: {
    color: "var(--Gray-700, #344054)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
    flex: 1,
  },
  timeContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  timeInput: {
    width: "120px",
  },
  timeLabel: {
    color: "var(--Gray-700, #344054)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "24px",
  },
};
